import { Box, Typography } from "@mui/material";
import { useStyles } from "./FailureCodesByFailureType.styles";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { BarsChartFailureCodes } from "components/BarsChartFailureCodes/BarsChartFailureCodes.view";
import { IChartBar, IFailureCodesByType } from "./FailureCodesByFailureTypeInterfaces";
import { useTranslation } from "react-i18next";
import NoData from "components/NoData/NoData.view";
import { IFailureCodesByFailureTypes } from "../../../LiveDowntimeReport/DowntimeReport/Components/TopFiveFailureCodes/TopFiveFailureCodesInterfaces";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { initialMillFilters } from "../SummaryReportFilters/useMillFilters";

const FailureCodesByFailureType = () => {
    const classes = useStyles();
    const { millId } = useParams();
    const { filters } = useContext(SummaryReportDashboardContext);
    const queryFilters = useMemo(
        () => ({
            ...filters,
            startDate: filters.startDate ? filters.startDate : initialMillFilters.startDate,
            endDate: filters.endDate ? filters.endDate : initialMillFilters.endDate,
            displayIn: filters.displayIn ? filters.displayIn : initialMillFilters.displayIn,
        }),
        [filters],
    );
    const { failureCodesByFailureType } = MillsEntity.GetFailureCodesByFailureType(Number(millId), queryFilters);
    const { t } = useTranslation();
    const [noInitialDataInMinutes, setNoInitialDataInMinutes] = useState<boolean>(false);

    const failureCodesByFailureTypeName: { [key: string]: IFailureCodesByType[] } = useMemo(() => {
        const allFailureCodesByFailureType = failureCodesByFailureType
            .filter(
                (failureType) =>
                    failureType.failure_code?.failure_code_id &&
                    failureType.failure_code?.failure_code_id !== undefined,
            )
            .map((failureType) => {
                return {
                    failure_type_id: failureType.failure_type.failure_type_id,
                    failure_type_name: failureType.failure_type.name,
                    machine_center_id: failureType.machine_center.machine_center_id,
                    color_code: failureType.failure_type.color_code,
                    failure_code_id: failureType.failure_code?.failure_code_id,
                    failure_code_name: failureType.failure_code?.name,
                    total_occurrences: failureType.occurrences,
                    total_minutes: failureType.seconds,
                };
            })
            .reduce((acc: { [key: string]: IFailureCodesByType[] }, obj) => {
                (acc[obj.failure_type_name] = acc[obj.failure_type_name] || []).push(obj);
                return acc;
            }, {});

        const aggregatedFailureCodesByType = Object.values(allFailureCodesByFailureType).reduce(
            (acc: { [key: string]: IFailureCodesByType[] }, obj: IFailureCodesByType[]) => {
                obj.forEach((failureCode) => {
                    const key = failureCode.failure_type_name;
                    const existingCode = acc[key]?.find((item) => item.failure_code_id === failureCode.failure_code_id);

                    if (existingCode) {
                        existingCode.total_occurrences += failureCode.total_occurrences;
                        existingCode.total_minutes += failureCode.total_minutes!;
                    } else {
                        acc[key] = acc[key] || [];
                        acc[key].push(failureCode);
                    }
                });

                return acc;
            },
            {},
        );

        Object.values(aggregatedFailureCodesByType).forEach((failureType) =>
            failureType.forEach(
                (failureCode) =>
                    (failureCode.total_minutes =
                        Math.round(failureCode.total_minutes / 60) < 1
                            ? 1
                            : Math.round(failureCode.total_minutes / 60)),
            ),
        );

        return aggregatedFailureCodesByType;
    }, [failureCodesByFailureType]);

    const failureCodesByMinutesOrOccurrences = useMemo(() => {
        const data =
            queryFilters.displayIn === "minutes"
                ? Object.values(failureCodesByFailureTypeName).map((failureCodes) =>
                      Object.values(failureCodes)
                          .sort((a, b) => b.total_minutes - a.total_minutes)
                          .slice(0, 5)
                          .reverse()
                          .reduce((acc: { [key: string]: IFailureCodesByType[] }, obj: IFailureCodesByType) => {
                              (acc[obj.failure_type_name] = acc[obj.failure_type_name] || []).push(obj);
                              return acc;
                          }, {}),
                  )
                : Object.values(failureCodesByFailureTypeName).map((failureCode) =>
                      Object.values(failureCode)
                          .sort((a, b) => b.total_occurrences - a.total_occurrences)
                          .slice(0, 5)
                          .reverse()
                          .reduce((acc: { [key: string]: IFailureCodesByType[] }, obj: IFailureCodesByType) => {
                              (acc[obj.failure_type_name] = acc[obj.failure_type_name] || []).push(obj);
                              return acc;
                          }, {}),
                  );

        return data;
    }, [queryFilters.displayIn, failureCodesByFailureTypeName]);

    const graphData: IFailureCodesByFailureTypes[] = useMemo(() => {
        const allFailureCodesByFailureTypesArray: IFailureCodesByFailureTypes[] = [];
        failureCodesByMinutesOrOccurrences.forEach((failureCode: { [key: string]: IFailureCodesByType[] }) => {
            for (const [key, value] of Object.entries(failureCode)) {
                const failureCodesByFailureType: IChartBar[] = [];
                Object.values(value).forEach((valueObject) => {
                    const failureCodeObject: IChartBar = {};
                    if (
                        (queryFilters.displayIn === "minutes" && valueObject.total_minutes) ||
                        queryFilters.displayIn === "occurrences"
                    ) {
                        failureCodeObject["failureCodeName"] = valueObject.failure_code_name;
                        failureCodeObject[`${valueObject.failure_code_name}Color`] = valueObject.color_code;
                        failureCodeObject[`${valueObject.failure_code_name}`] =
                            queryFilters.displayIn === "minutes"
                                ? valueObject.total_minutes
                                : valueObject.total_occurrences;
                        failureCodesByFailureType.push(failureCodeObject);
                    }
                });
                allFailureCodesByFailureTypesArray.push({
                    failureType: key,
                    failureCodes: failureCodesByFailureType,
                });
            }
        });

        return allFailureCodesByFailureTypesArray;
    }, [queryFilters.displayIn, failureCodesByMinutesOrOccurrences]);

    useEffect(() => {
        if (graphData) {
            setNoInitialDataInMinutes(graphData.every((item) => item.failureCodes.length === 0));
        }
    }, [graphData]);

    const sortedFailureTypes = useMemo(() => {
        const sortedData = graphData.sort((a: IFailureCodesByFailureTypes, b: IFailureCodesByFailureTypes) => {
            const sumA = Object.values(a.failureCodes).reduce((acc: number, current: IChartBar) => {
                const numericValues = Object.values(current).filter((val) => typeof val === "number") as number[];
                return acc + numericValues.reduce((sum, val) => sum + val, 0);
            }, 0);
            const sumB = Object.values(b.failureCodes).reduce((acc: number, current: IChartBar) => {
                const numericValues = Object.values(current).filter((val) => typeof val === "number") as number[];
                return acc + numericValues.reduce((sum, val) => sum + val, 0);
            }, 0);
            return sumB - sumA;
        });

        return sortedData;
    }, [graphData]);

    return (
        <Box className={classes.container} sx={{ flexDirection: "column" }}>
            <Box className={classes.titleContainer}>
                <Typography className={classes.title} variant="subtitle1">
                    {t("locations.summary_report.failure_codes_downtime_type.title")}
                </Typography>
            </Box>
            {!sortedFailureTypes.length || noInitialDataInMinutes ? (
                <Box className={classes.noData}>
                    <NoData />
                </Box>
            ) : (
                <Box className={classes.graphContainer}>
                    {Object.values(sortedFailureTypes).map((failureTypeAndCodes, index) => {
                        if (
                            failureTypeAndCodes.failureCodes[0] &&
                            Object.values(failureTypeAndCodes.failureCodes[0]).some(
                                (item: string | number | null) => typeof item === "number" && !isNaN(item) && item > 0,
                            )
                        ) {
                            return (
                                <div key={`${index}_${Math.random()}`} className={classes.barGraphWrapper}>
                                    <Typography variant="subtitle1" className={classes.graphTitle}>
                                        {failureTypeAndCodes.failureType}
                                    </Typography>
                                    <BarsChartFailureCodes data={failureTypeAndCodes.failureCodes} />
                                </div>
                            );
                        } else {
                            return null;
                        }
                    })}
                </Box>
            )}
        </Box>
    );
};

export default FailureCodesByFailureType;
